import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import WorksheetCard from '../WorksheetCard';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

// eslint-disable-next-line no-unused-vars
function Strengths({ goBack, goHome }) {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCards, setSelectedCards] = useState([]);
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [isLoadingPickedCards, setIsLoadingPickedCards] = useState(false);
    const [clickedCardId, setClickedCardId] = useState(null);
    const [shuffledCards, setShuffledCards] = useState([]);

    const retrySupabaseQuery = useCallback(async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);

    const fetchCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Astrology') // Changed from 'Strengths' to 'Astrology'
            );
            if (error) throw error;
            setCards(data);
        } catch (err) {
            alert('Failed to load cards.');
            console.error('Error fetching cards:', err.message);
        } finally {
            setLoading(false);
        }
    }, [retrySupabaseQuery]);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('component', 'Strengths')
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, [retrySupabaseQuery]);

    useEffect(() => {
        fetchCards();
        fetchSavedReadings();
    }, [fetchCards, fetchSavedReadings]);

    useEffect(() => {
        if (selectedCards.length > 0) {
            setIsLoadingPickedCards(true);
            const timer = setTimeout(() => setIsLoadingPickedCards(false), 500);
            return () => clearTimeout(timer);
        }
    }, [selectedCards]);

    const handleCardClick = (card) => {
        if (selectedCards.length < 8 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    const handleSaveReading = async () => {
        if (selectedCards.length !== 8) {
            alert('Please select exactly 8 cards to save your reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in. Please log in to save your reading.');
                return;
            }

            // Check the number of existing readings using the utility
            const canSave = await checkReadingLimit(user.id, 'Strengths', retrySupabaseQuery, supabase);
            if (!canSave) {
                alert('You have reached the limit of 10 saved readings for this picker. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = selectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Strengths?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'Strengths',
                cards: selectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            alert('Reading saved successfully!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            alert('Failed to save reading. Please try again.');
            console.error('Error saving reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadReading = (reading) => {
        // Prevent loading a saved reading before cards are fetched
        if (loading || cards.length === 0) {
            alert('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = (reading.cards || []).map((cardId) => cardsById[cardId]).filter(Boolean);

        if (loadedCards.length !== (reading.cards || []).length) {
            alert('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards.map(card => ({
            ...card,
            descriptions: card.descriptions || 'No description available'
        })));
        setPickerVisible(false);
    };

    const handleSaveProgress = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in. Please log in to save progress.');
                return;
            }

            const progressData = {
                user_id: user.id,
                current_component: 'Strengths',
                animated_helper: 'no', // always set to 'no'
                timestamp: new Date().toISOString(),
            };

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert(progressData);

            if (progressError) {
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            alert('Session progress has been saved successfully!');
        } catch (err) {
            alert('Failed to save progress. Please try again.');
            console.error('Error saving progress:', err.message);
        }
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible((prev) => {
            const next = !prev;
            // Shuffle cards every time picker is shown
            if (!prev && cards.length > 0) setShuffledCards(shuffleArray([...cards]));
            return next;
        });
    };

    // Helper to filter out back images from card set
    const isNotBackImage = (card) => {
        if (!card.image_path) return true;
        return !card.image_path.toLowerCase().includes('astrology back');
    };

    return (
        <>
            <style>
                {`
                @media (max-width: 600px) {
                    .strengths-btn-row {
                        flex-direction: row !important;
                        gap: 6px !important;
                    }
                    .strengths-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                    .strengths-card-picker {
                        display: flex !important;
                        flex-direction: column !important;
                        flex-wrap: nowrap !important;
                        overflow-x: visible !important;
                        max-width: 100vw !important;
                        padding-bottom: 8px !important;
                        align-items: flex-start !important;
                        margin-left: 0 !important;
                    }
                    .strengths-card-row {
                        display: flex !important;
                        flex-direction: row !important;
                        flex-wrap: nowrap !important;
                        margin-top: 0 !important;
                        margin-bottom: 8px !important;
                        width: 100vw !important;
                        min-width: 100vw !important;
                        justify-content: flex-start !important;
                        align-items: flex-end !important;
                        overflow-x: hidden !important;
                    }
                    .strengths-card {
                        width: 48px !important;
                        min-width: 48px !important;
                        height: 64px !important;
                        margin-left: -36px !important;
                        margin-right: 0 !important;
                        transition: transform 0.2s, box-shadow 0.2s;
                        z-index: 1;
                    }
                    .strengths-card:first-child {
                        margin-left: 0 !important;
                    }
                }
                `}
            </style>
            <div style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
            }}>
                <h3 style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: 'black',
                    marginBottom: '32px',
                }}>
                    Strengths
                </h3>

                {/* Session Helper Cards Row */}
                <div
                    className="strengths-session-helper-row"
                    style={{
                        display: 'flex',
                        flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '16px',
                    }}
                >
                    {/* Left Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div>• Strengths are innate or developed abilities that energize us and enable us to perform at our best in specific areas.</div>
                            <div>• Recognizing strengths requires self-reflection to identify moments when we feel confident, engaged, and impactful.</div>
                            <div>• Acknowledging strengths boosts self-esteem and clarifies our unique contributions to personal and professional contexts</div>
                            <div>• Strengths often manifest in patterns of success across different situations, even if we don’t initially label them as such.</div>
                            <div>• Identifying strengths involves noticing what others praise or rely on us for, as external feedback can highlight blind spots.</div>
                            <div>• Embracing strengths empowers us to align our actions with our values, creating a life that feels authentic and fulfilling.</div>
                        </div>
                    </div>
                    {/* Right Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div>1. What activities make you lose track of time because you’re so absorbed, and what skills are you using in those moments?</div>
                            <div>2. When have you felt most proud of something you accomplished, and what personal qualities made that success possible?</div>
                            <div>3. What do people consistently ask for your help with, and how does that reflect a strength you bring to those situations?</div>
                            <div>4. Think of a time you overcame a challenge—what ability or trait did you rely on to navigate it successfully?</div>
                            <div>5. What part of your daily routine or work feels most natural and energizing, and what does that reveal about your strengths?</div>
                            <div>6. If you could teach others one thing you’re really good at, what would it be, and why do you excel in that area?</div>
                        </div>
                    </div>
                </div>

                {/* WorksheetCard Section */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '24px',
                }}>
                    <WorksheetCard title="Strengths Worksheet" columnName="strengths" />
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                }}>
                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            marginBottom: '16px',
                        }}>
                            When we do a self-examination sometimes we know what our strengths are and sometimes we find it difficult to identify what our strengths are. Is there something you often get recognized for by others? Maybe you never get recognized and your strength is being stealth!
                        </p>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            marginBottom: '16px',
                        }}>
                            List as many strengths as you can. Know this list can change and should be updated from time to time. These identified strengths are the things you can take into consideration as you make decisions in life. When you offer assistance to others it is often going to be something that is also your strength.
                        </p>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            marginBottom: '16px',
                        }}>
                            So, if someone needs help moving and you are not that strong in packing boxes but you are really good at stacking the boxes in the truck in the most efficient manner, then you offer that. Your strengths can be things that you worked hard to become strong in or things that came naturally to you.
                        </p>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            marginBottom: '16px',
                        }}>
                            Sometimes it will be important to recognize that you are strong in a certain way that others are not, so that you can adjust the agreements you make with them. Even your strengths leave room for improvement and practice.
                        </p>
                    </div>

                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: 'black',
                        }}>
                            Learn how to leverage your strengths to achieve your goals.
                        </p>
                    </div>

                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: 'black',
                        }}>
                            The Oracle card deck below uses astrology terms and symbols and can be used to help you identify your strengths.
                        </p>
                    </div>
                </div>

                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                    }}
                    onClick={handleStartReading}
                >
                    {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
                </button>

                {/* Card Picker - mobile rows of 20 fanned cards, browser unchanged */}
                {pickerVisible && !loading && (
                    <div
                        className="strengths-card-picker"
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            maxWidth: '1200px',
                            margin: '0 auto',
                            padding: '8px 16px',
                            overflowX: 'hidden',
                        }}>
                        {(() => {
                            const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
                            const cardSet = (shuffledCards.length > 0 ? shuffledCards : cards).filter(isNotBackImage);
                            let rows = [];
                            if (isMobile) {
                                // For mobile, split into rows of 20
                                for (let i = 0; i < cardSet.length; i += 20) {
                                    rows.push(cardSet.slice(i, i + 20));
                                }
                            } else {
                                // For desktop, 71 per row
                                rows = cardSet.reduce((acc, card, index) => {
                                    const rowIndex = Math.floor(index / 71);
                                    if (!acc[rowIndex]) acc[rowIndex] = [];
                                    acc[rowIndex].push(card);
                                    return acc;
                                }, []);
                            }
                            return rows.map((row, rowIndex) => (
                                <div
                                    key={rowIndex}
                                    className={isMobile ? "strengths-card-row" : undefined}
                                    style={{
                                        display: 'flex',
                                        flex: '0 0 100%',
                                        marginTop: rowIndex === 0 ? 0 : '-10px',
                                        overflow: isMobile ? 'hidden' : 'hidden',
                                    }}
                                >
                                    {row.map((card, index) => (
                                        <div
                                            key={card.id}
                                            className={isMobile ? "strengths-card" : undefined}
                                            style={{
                                                width: isMobile ? '48px' : '120px',
                                                height: isMobile ? '64px' : '100px',
                                                minWidth: isMobile ? '48px' : '120px',
                                                marginLeft: isMobile
                                                    ? (index === 0 ? 0 : '-36px')
                                                    : (index === 0 ? 0 : '-100px'),
                                                marginRight: 0,
                                                cursor: 'pointer',
                                                boxShadow: clickedCardId === card.id
                                                    ? '0 0 10px rgba(0, 255, 0, 0.5)'
                                                    : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                transform: clickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                                zIndex: 1,
                                                backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Astrology/Astrology%20Back00.webp)',
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                borderRadius: '8px',
                                            }}
                                            onClick={() => handleCardClick(card)}
                                        />
                                    ))}
                                </div>
                            ));
                        })()}
                    </div>
                )}

                {selectedCards.filter(isNotBackImage).length > 0 && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gap: '4px',
                        marginTop: '16px',
                        background: 'white',
                        border: '2px solid black',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '8px',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '16px auto',
                        width: '100%',
                    }}>
                        {selectedCards.filter(isNotBackImage).map((card, index) => {
                            const labels = [
                                'Known Skill',
                                'Practicing Skill',
                                'Subconscious Strength',
                                'Easy Progress',
                                'Compensating For',
                                'Natural Talent',
                                'Supporting You',
                                'Example to Others'
                            ];
                            return (
                                <div key={card.id} style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                }}>
                                    <img
                                        src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                        alt={card.title}
                                        style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                        onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                    />
                                    <p style={{
                                        fontFamily: 'Times New Roman, Times, serif',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        marginTop: '8px',
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        maxWidth: '100px',
                                    }}>
                                        {labels[index] || 'No description available'}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                {selectedCards.length === 8 && (
                    <button
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: 'auto',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            margin: '16px auto',
                        }}
                        onClick={handleSaveReading}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Reading'}
                    </button>
                )}

                <div
                    className="strengths-btn-row"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        marginTop: '32px',
                    }}>
                    <button
                        className="strengths-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goBack}
                    >Back</button>
                    <button
                        className="strengths-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goHome}
                    >Home</button>
                    <button
                        className="strengths-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={handleSaveProgress}
                    >Save</button>
                </div>

                {savedReadings.length > 0 && (
                    <div style={{
                        marginTop: '32px',
                        width: '100%',
                        maxWidth: '600px',
                        padding: '8px',
                    }}>
                        <h4 style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            marginBottom: '16px',
                        }}>
                            Saved Readings
                        </h4>
                        {savedReadings.map((reading) => (
                            <div
                                key={reading.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px',
                                    border: '1px solid black',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '14px',
                                }}
                            >
                                <span onClick={() => handleLoadReading(reading)}>
                                    {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReading(reading.id, fetchSavedReadings, retrySupabaseQuery, supabase);
                                }}
                            >
                                -delete
                            </span>
                        </div>
                        ))}
                    </div>
                )}
            </div>
            <div style={{ minHeight: 30 }}>
                {isLoadingPickedCards && (
                    <div style={{ color: '#4B5563', fontSize: 18, fontFamily: 'Times New Roman, Times, serif', marginBottom: 12 }}>
                        Loading picked cards...
                    </div>
                )}
            </div>
        </>
    );
}

export default Strengths;